import { localizedStrings } from '@core/strings'
import React from 'react'
import { useModal } from '@common/GlobalModal'
import { Box, Flex } from '@fundamentals'
import { Button, IconButton } from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import { PageTitle } from '@elements'
import { RegisterItemDetailsSidebar } from '@modules/registers/items/RegisterItemDetailsSidebar'
import { useGetRegisterItem } from '@core/react-query/features/registers/registers/register/items/item'
import BackdropLoader from '@common/fundamentals/BackdropLoader'
import ArrowBack from '@mui/icons-material/ArrowBack'
import { useNavigationContext } from '@common/hooks'
import { useGetRegisterTemplate } from '@core/react-query/features/registers/templates'
import { useGetRegisterDetails } from '@core/react-query/features/registers/registers/register/hooks'
import { useRouter } from 'next/router'
import { PageUrls } from '@core/page-urls'
import { useToast } from '@core/toast'
import { RegisterFilterScopes } from '../types'
import { RegisterItemDetailsActions } from '@modules/registers/items/RegisterItemDetailsActions'
import { Palette } from '@core/theme/color-palette-siteassist'
import useConstants from '@core/providers/useConstants'
import { RegisterItemDetailsTabs } from './RegisterItemDetailsTabs'
import { useProductAccess } from 'webapp/providers/authentication'
import { useDrawer } from '@common/GlobalDrawer'
import { RegisterInspectionSidePanel } from '@modules/registers/details/registerInspections/RegisterInspectionSidePanel'
import type { InspectionDetailsClickParams } from '@modules/registers/details/registerInspections/RegisterScheduleInspectionsCard'

/** Same value as the register table item-details drawer (`MIN_SIZE_SO_ALL_TABS_ARE_VISIBLE`). */
const INSPECTION_SIDE_PANEL_WIDTH = 490

type RegisterItemDetailsProps = {
  scope: RegisterFilterScopes
  registerId: number
  registerItemId: number
  registerViewId?: number
  isModal?: boolean
  initialTabColumnId?: string
}
export const RegisterItemDetails: React.FC<RegisterItemDetailsProps> = ({
  scope,
  registerId,
  registerItemId,
  registerViewId,
  isModal,
  initialTabColumnId,
}) => {
  const router = useRouter()
  const { organisationId } = useNavigationContext()
  const { hideModal } = useModal()
  const { showDrawer, hideDrawer } = useDrawer()
  const toast = useToast()
  const { constants } = useConstants()
  const navBarHeight = constants.nav_bar_height
  const bgColor = isModal ? Palette.Common.White : Palette.Grey[50]
  const { isViewMode } = useProductAccess()

  const { data: registerItem, isFetching } = useGetRegisterItem(
    {
      registerId,
      registerItemId,
    },
    {
      onError: (error) => toast.error(error?.message),
    },
  )

  const { data: register } = useGetRegisterDetails({
    registerId,
  })

  const { data: registerTemplate } = useGetRegisterTemplate(
    {
      organisationId,
      templateId: register?.registerTemplate?.id,
    },
    { enabled: !!register?.registerTemplate?.id },
  )

  const stickyPosition = {
    HEADER: isModal ? 0 : `calc(${navBarHeight}px + 16px)`,
    TAB_HEADER: isModal ? '96px' : '120px',
  }

  const handleInspectionDetailsClick = (
    params: InspectionDetailsClickParams,
  ) => {
    showDrawer(
      <RegisterInspectionSidePanel
        width={INSPECTION_SIDE_PANEL_WIDTH}
        registerId={registerId}
        registerItemId={registerItemId}
        projectId={registerItem?.register?.projectId}
        config={params.config}
        scheduledInspection={params.scheduledInspection}
        inspection={params.inspection}
        initialTab={params.initialTab}
        onClose={hideDrawer}
      />,
      { variant: 'persistent', transitionDuration: 200 },
    )
  }

  // The global drawer persists across route changes, so close it on navigation
  // to avoid leaving a stale inspection panel visible (e.g. on back button).
  React.useEffect(() => {
    const handleRouteChange = () => {
      hideDrawer()
    }
    router.events.on('routeChangeStart', handleRouteChange)
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.events, hideDrawer])

  return (
    <>
      <BackdropLoader open={!registerItem && isFetching} />
      {!!registerItem && (
        <>
          <Box
            data-test='registerItemDetails'
            position='sticky'
            top={stickyPosition.HEADER}
            zIndex={10}
            bgcolor={bgColor}
            px='3'
          >
            {isModal && (
              <Flex justifyContent='space-between'>
                <PageTitle title={localizedStrings.registerItemDetails} />
                <IconButton aria-label='close-modal' onClick={hideModal}>
                  <ClearIcon />
                </IconButton>
              </Flex>
            )}
            <Flex justifyContent='space-between' pb={2}>
              <Flex alignItems='center'>
                {!isModal && !isViewMode && (
                  <Button
                    variant='outlined'
                    size='medium'
                    startIcon={<ArrowBack />}
                    sx={{
                      '.MuiButton-startIcon': { margin: 0 },
                      py: 0.3,
                      px: 0.9,
                      minWidth: 0,
                      mr: 2,
                    }}
                    onClick={() => {
                      const commonArgs = {
                        organisationId,
                        registerId,
                      }
                      const url =
                        scope.type === 'PROJECT'
                          ? PageUrls.projectRegisterDetails({
                              ...commonArgs,
                              projectId: scope.projectId,
                            })
                          : PageUrls.registerDetails(commonArgs)

                      router.push(url)
                    }}
                    data-test='dd-previous-page'
                  />
                )}

                <PageTitle
                  sx={{
                    m: 0,
                  }}
                  title={register?.name}
                />
              </Flex>
              {!isViewMode && (
                <RegisterItemDetailsActions
                  scope={scope}
                  register={register}
                  registerItem={registerItem}
                  registerTemplate={registerTemplate}
                  registerViewId={registerViewId}
                />
              )}
            </Flex>
          </Box>
          <Flex>
            <RegisterItemDetailsTabs
              registerId={registerId}
              registerItemId={registerItemId}
              registerItem={registerItem}
              organisationId={organisationId}
              isModal={isModal}
              stickyPosition={stickyPosition.TAB_HEADER}
              bgColor={bgColor}
              onInspectionDetailsClick={handleInspectionDetailsClick}
              initialTabColumnId={initialTabColumnId}
            />
            <Box
              pl={3}
              width='20rem'
              position='sticky'
              top={stickyPosition.TAB_HEADER}
              maxHeight={`calc(100vh - ${stickyPosition.TAB_HEADER})`}
              overflow='auto'
            >
              <RegisterItemDetailsSidebar registerItem={registerItem} />
            </Box>
          </Flex>
        </>
      )}
    </>
  )
}
